// Наши группы мышц (backend domain.training.MUSCLES) → мышцы фигуры Open-Muscle-Map и тона подсветки.

export type Sex = 'male' | 'female'

/** Ранг прокачки мышцы (по уровню, как MUSCLE_RANKS на сервере). */
export type MuscleRank = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'mythic'

/** Тон мышцы: тёплые — работала, холодные — не работала или давно без нагрузки. */
export type MuscleTone =
  | 'primary'
  | 'secondary'
  | 'idle'
  | 'forgot'
  | 'stale'
  | 'low'
  | 'ok'
  | 'over'
  | 'fatigued'
  | 'recovering'
  | 'fresh'
  | 'planned'
  | MuscleRank

/** Группа мышц → data-muscle-id фигуры. Шея, голени, суставы и связки остаются нейтральными. */
export const FIGURE_MUSCLES: Record<string, readonly string[]> = {
  chest: ['pectoralis_major'],
  lats: ['latissimus_dorsi'],
  upper_back: ['trapezius', 'posterior_rotator_cuff'],
  lower_back: ['erector_spinae'],
  front_delts: ['anterior_deltoid'],
  side_delts: ['lateral_deltoid'],
  rear_delts: ['posterior_deltoid'],
  biceps: ['biceps_brachii', 'brachialis'],
  triceps: ['triceps_brachii'],
  forearms: ['forearm_flexors', 'forearm_extensors'],
  abs: ['rectus_abdominis'],
  obliques: ['external_oblique', 'serratus_anterior'],
  quads: ['rectus_femoris', 'vastus_lateralis', 'vastus_medialis'],
  hamstrings: ['biceps_femoris', 'medial_hamstrings'],
  glutes: ['gluteus_maximus'],
  adductors: ['hip_adductors'],
  abductors: ['gluteus_medius', 'tensor_fasciae_latae'],
  calves: ['gastrocnemius', 'soleus'],
}

export const GROUP_OF_FIGURE: Record<string, string> = Object.fromEntries(
  Object.entries(FIGURE_MUSCLES).flatMap(([group, ids]) => ids.map((id) => [id, group])),
)

export const TONE_LABELS: Record<MuscleTone, string> = {
  primary: 'Основная',
  secondary: 'Вспомогательная',
  idle: 'Не работала',
  forgot: '14+ дней без нагрузки',
  stale: '7–13 дней',
  low: '1–9 подходов',
  ok: '10–20 — норма',
  over: 'Больше 20',
  fatigued: 'Устала, до 40 %',
  recovering: 'Восстанавливается',
  fresh: 'Свежая, от 90 %',
  planned: 'Ещё впереди',
  bronze: 'Бронза 1–4',
  silver: 'Серебро 5–9',
  gold: 'Золото 10–14',
  platinum: 'Платина 15–19',
  diamond: 'Алмаз 20–29',
  mythic: 'Мифический 30+',
}

/** Тон мышцы за неделю: подходы за 7 дней (основная 1, вспомогательная 0.5) и дни без нагрузки. */
export function weekTone(sets: number, daysSince: number | null): MuscleTone {
  if (sets <= 0) return daysSince != null && daysSince < 14 ? 'stale' : 'forgot'
  if (sets < 10) return 'low'
  return sets <= 20 ? 'ok' : 'over'
}

/** Тоны упражнения: основная и вспомогательные; остальные — без тона. */
export function exerciseTones(primary: string, secondary: readonly string[]): Record<string, MuscleTone> {
  const tones: Record<string, MuscleTone> = {}
  for (const m of secondary) tones[m] = 'secondary'
  tones[primary] = 'primary'
  if (primary === 'full_body') for (const m of Object.keys(FIGURE_MUSCLES)) tones[m] = 'primary'
  return tones
}

/** Добавить группу в выбор или убрать, если уже выбрана. */
export function toggleMuscle(list: readonly string[], key: string): string[] {
  return list.includes(key) ? list.filter((k) => k !== key) : [...list, key]
}

/** Свежая мышца — от этого процента восстановления (как READY на сервере). */
export const READY_PERCENT = 90

/** Тон мышцы по восстановлению, %. */
export function recoveryTone(percent: number): MuscleTone {
  if (percent >= READY_PERCENT) return 'fresh'
  return percent < 40 ? 'fatigued' : 'recovering'
}

/** Когда мышца станет свежей: «через 5 ч», «через 1 д 17 ч», «через 2 д». */
export function readyIn(hours: number): string {
  const d = Math.floor(hours / 24)
  const h = hours % 24
  return `через ${d ? `${d} д` : ''}${d && h ? ' ' : ''}${h || !d ? `${h} ч` : ''}`
}

export const RANKS: readonly MuscleRank[] = ['bronze', 'silver', 'gold', 'platinum', 'diamond', 'mythic']
